import { useMemo } from 'react';
import { createFilter } from 'react-select';
import get from 'lodash/get';

import { ICitySatellite } from '@r1-frontend/api-domru/api-content/v1/geography/get-cities-satellites';
import { IRegionsWithCitiesGroup, IRegionWithCities } from '@r1-frontend/api-domru/api-content/v1/geography/get-regions/dto/ResponseDto';

import RegionElement from '@r1-frontend/city-select/components/RegionElement';
import TopCities from '@r1-frontend/city-select/components/TopCities';
import { getOptionsBySatellites, ICityOption } from '@r1-frontend/city-select/helpers/getOptionsBySatellites';
import { useColumnsByRegionLetters } from '@r1-frontend/city-select/hooks/useColumnsByRegionLetters';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import Throbber from '@r1-frontend/ui-react/components/loaders/Throbber';
import { ArrowLeft } from '@r1-frontend/ui-react/components/svg/arrows';
import { H2 } from '@r1-frontend/ui-react/components/typography/heading';

import * as ST from './styled';

const noOptionsMessage = (message: string, field?: ICityOption) => (inputValue: string) =>
    inputValue === '' || (get(field, 'label', '') === inputValue) ? message : 'Не найдено';

const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'start' as const,
};

interface IProps {
    regions: IRegionsWithCitiesGroup,
    topCities: ICitySatellite[],
    citiesSatellites: ICitySatellite[],
    selectedRegion: IRegionWithCities | null,
    onRegionSelect: (region: IRegionWithCities) => void,
    onCitySelect: (city?: ICitySatellite) => void,
    city?: ICityOption,
}

const CitySelect = ({
    regions,
    topCities,
    citiesSatellites,
    selectedRegion,
    onRegionSelect,
    onCitySelect,
    city,
}: IProps): JSX.Element => {
    const citiesOptions = useMemo<ICityOption[]>(() => getOptionsBySatellites(citiesSatellites), [citiesSatellites]);

    const lettersByColumns = useColumnsByRegionLetters({ regions, topCities });

    const onChangeCity = (selectedCityOption: ICityOption) => {
        const { label, value, provider_id, has_sso } = selectedCityOption;
        onCitySelect({ name: `${label}`, domain: value, provider_id, has_sso });
    };

    return (
        <ST.Container>
            <ST.GlobalStyle $active/>
            <ST.ContentWrapper>
                <Button
                    size={'medium'}
                    bType={'ghost'}
                    Icon={<ArrowLeft size={24}/>}
                    onClick={() => onCitySelect()}
                >
                    Назад
                </Button>

                <H2 $withIndent={false}>Выберите город</H2>

                {(!!lettersByColumns.length && !!topCities.length && !!citiesSatellites.length)
                    ? <>
                        <ST.DropDown
                            value={city}
                            onChange={onChangeCity}
                            options={citiesOptions}
                            placeholder={'Ваш город'}
                            noOptionsMessage={noOptionsMessage('Введите город', city)}
                            isSearchable={true}
                            filterOption={createFilter(filterConfig)}
                        />
                        <ST.ColumnsWrapper>
                            {lettersByColumns.map((lettersColumn, columnIndex) => {
                                const columnLetters = Object.keys(lettersColumn);

                                return (
                                    <ST.Column key={columnIndex}>
                                        {columnIndex === 0 && (
                                            <TopCities
                                                topCities={topCities}
                                                onCitySelect={onCitySelect}
                                            />
                                        )}
                                        {columnLetters.map(letter => (
                                            <ST.RegionsWrapper key={letter} data-test='regions-wrap'>
                                                <ST.RegionsLetter data-test='letter'>{letter}</ST.RegionsLetter>
                                                {lettersColumn[letter].map(region => (
                                                    <RegionElement
                                                        key={region.region_id}
                                                        isRegionOpen={region.region_id === selectedRegion?.region_id}
                                                        region={region}
                                                        onRegionSelect={onRegionSelect}
                                                        onCitySelect={onCitySelect}
                                                    />
                                                ))}
                                            </ST.RegionsWrapper>
                                        ))}
                                    </ST.Column>
                                );
                            })}
                        </ST.ColumnsWrapper>
                    </>
                    : <Throbber/>
                }
            </ST.ContentWrapper>
        </ST.Container>
    );
};

export default CitySelect;
